import { useRef, useState, useCallback, useEffect } from "react";

interface UseResizeOptions {
  initialSize: number;
  minSize: number;
  maxSize: number;
  direction: "horizontal" | "vertical";
  /** Для правой панели: тянем влево = шире, вправо = уже */
  invertHorizontal?: boolean;
  /** Вызывается при окончании ресайза (mouseup/touchend) с финальным размером — для сохранения в localStorage */
  onSizeChange?: (size: number) => void;
}

export const useResize = ({ initialSize, minSize, maxSize, direction, invertHorizontal = false, onSizeChange }: UseResizeOptions) => {
  const [size, setSize] = useState(initialSize);
  const [isResizing, setIsResizing] = useState(false);
  const elementRef = useRef<HTMLDivElement | null>(null);
  const isResizingRef = useRef(false);
  const rafIdRef = useRef<number | null>(null);
  const sizeRef = useRef(initialSize);
  const startPosition = useRef(0);
  const startSize = useRef(0);
  const minSizeRef = useRef(minSize);
  const maxSizeRef = useRef(maxSize);
  const directionRef = useRef(direction);
  const invertRef = useRef(invertHorizontal);
  const onSizeChangeRef = useRef(onSizeChange);

  useEffect(() => {
    onSizeChangeRef.current = onSizeChange;
  }, [onSizeChange]);
  useEffect(() => {
    minSizeRef.current = minSize;
    maxSizeRef.current = maxSize;
    directionRef.current = direction;
    invertRef.current = invertHorizontal;
  }, [minSize, maxSize, direction, invertHorizontal]);

  const onMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    isResizingRef.current = true;
    setIsResizing(true);
    startPosition.current = directionRef.current === "horizontal" ? e.clientX : e.clientY;
    startSize.current = sizeRef.current;
    document.body.style.userSelect = "none";
    document.body.style.cursor = directionRef.current === "horizontal" ? "col-resize" : "row-resize";
  }, [size]);

  const onTouchStart = useCallback((e: React.TouchEvent) => {
    // Не вызываем preventDefault здесь; для touchmove слушаем на window с { passive:false }.
    e.stopPropagation();
    isResizingRef.current = true;
    setIsResizing(true);
    const touch = e.touches[0];
    startPosition.current = directionRef.current === "horizontal" ? touch.clientX : touch.clientY;
    startSize.current = sizeRef.current;
    document.body.style.userSelect = "none";
  }, []);

  const scheduleSizeUpdate = useCallback((nextSize: number) => {
    if (rafIdRef.current !== null) {
      cancelAnimationFrame(rafIdRef.current);
    }
    rafIdRef.current = requestAnimationFrame(() => {
      sizeRef.current = nextSize;
      setSize(nextSize);
      rafIdRef.current = null;
    });
  }, []);

  useEffect(() => {
    sizeRef.current = size;
  }, [size]);

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isResizingRef.current) return;

      const currentPosition = directionRef.current === "horizontal" ? e.clientX : e.clientY;
      let delta = currentPosition - startPosition.current;
      if (directionRef.current === "horizontal" && invertRef.current) delta = -delta;
      let newSize = startSize.current + delta;
      newSize = Math.max(minSizeRef.current, Math.min(maxSizeRef.current, newSize));
      scheduleSizeUpdate(newSize);
    };

    const handleTouchMove = (e: TouchEvent) => {
      if (!isResizingRef.current) {
        return;
      }
      e.preventDefault();

      const touch = e.touches[0];
      const currentPosition = directionRef.current === "horizontal" ? touch.clientX : touch.clientY;
      let delta = currentPosition - startPosition.current;
      if (directionRef.current === "horizontal" && invertRef.current) delta = -delta;
      let newSize = startSize.current + delta;
      newSize = Math.max(minSizeRef.current, Math.min(maxSizeRef.current, newSize));
      scheduleSizeUpdate(newSize);
    };

    const handleMouseUp = () => {
      if (!isResizingRef.current) return;
      isResizingRef.current = false;
      setIsResizing(false);
      document.body.style.userSelect = "";
      document.body.style.cursor = "";
      onSizeChangeRef.current?.(sizeRef.current);
    };

    const handleTouchEnd = () => {
      if (!isResizingRef.current) return;
      isResizingRef.current = false;
      setIsResizing(false);
      document.body.style.userSelect = "";
      onSizeChangeRef.current?.(sizeRef.current);
    };

    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);
    // Важно: ресайзер на мобиле может делать stopPropagation() на target,
    // поэтому слушаем touchmove/touchend в capture phase, чтобы не потерять события.
    window.addEventListener("touchmove", handleTouchMove, { passive: false, capture: true });
    window.addEventListener("touchend", handleTouchEnd, { capture: true });
    window.addEventListener("touchcancel", handleTouchEnd, { capture: true });

    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
      window.removeEventListener("touchmove", handleTouchMove, { capture: true } as EventListenerOptions);
      window.removeEventListener("touchend", handleTouchEnd, { capture: true } as EventListenerOptions);
      window.removeEventListener("touchcancel", handleTouchEnd, { capture: true } as EventListenerOptions);
      if (rafIdRef.current !== null) {
        cancelAnimationFrame(rafIdRef.current);
        rafIdRef.current = null;
      }
    };
  }, [scheduleSizeUpdate]); // Внутри используем refs

  return { size, elementRef, onMouseDown, onTouchStart, isResizing };
};

